import { OrderBookRealTimeClient } from '@katanaperps/katana-perps-sdk';
import { useEffect, useRef, useState } from 'react';

import { useActiveWindowInterval } from 'components/hooks';
import {
  createOrderbookClient,
  getBestPriceFromDisplayedOrderbook,
  useDisplayedOrderbook,
} from 'components/orders/orderBook/orderBookService';
import { middleOrderbookPrice } from 'components/orders/orderUtil';

/** Connect to existing orderbook or create new WS connection get get orderbook prices */
export function useConnectOrderbookAndGetMiddlePrice({
  market,
  startOrderbook,
}: {
  market: string;
  startOrderbook: boolean;
}) {
  const displayedOrderbook = useDisplayedOrderbook();
  const otherMarketOrderbook = useRef<OrderBookRealTimeClient>();

  /** Only start orderbook when needed. And keep it running afterwards even when `startOrderbook` is false */
  const [orderbookNeeded, setOrderbookNeeded] = useState(startOrderbook);
  useEffect(() => {
    if (startOrderbook && !orderbookNeeded) {
      setOrderbookNeeded(true);
    }
  }, [orderbookNeeded, startOrderbook]);

  const [middlePrice, setMiddlePrice] = useState<number | undefined>();

  useEffect(() => {
    if (!orderbookNeeded || displayedOrderbook.serviceMarket === market) {
      return;
    }
    const client = createOrderbookClient();
    otherMarketOrderbook.current = client;
    client.start([market]);
    // client.on('l2', async (market: string) => {
    //   const response = await client.getOrderBookL2(market, 2);
    //   setMiddlePrice([response?.asks[0][0], response?.bids[0][0]]);
    // });
    return () => {
      console.log('Stopping ws connection...');
      otherMarketOrderbook.current = undefined;
      client.removeAllListeners();
      client.stop();
      client.on('connected', () => {
        client.stop();
      });
    };
  }, [orderbookNeeded, displayedOrderbook.serviceMarket, market]);

  useEffect(() => {
    setMiddlePrice(undefined);
  }, [market]);

  useActiveWindowInterval(() => {
    if (!orderbookNeeded) {
      return;
    }
    if (displayedOrderbook.serviceMarket === market) {
      setMiddlePrice(
        middleOrderbookPrice(
          getBestPriceFromDisplayedOrderbook('ask'),
          getBestPriceFromDisplayedOrderbook('bid')
        )
      );
      return;
    }
    otherMarketOrderbook.current?.getOrderBookL2(market, 2).then(response => {
      if (!response?.asks || !response?.bids) {
        console.warn('Missing orderbook');
        return;
      }
      const bestAsk = response.asks?.[0]?.[0];
      const bestBid = response.bids?.[0]?.[0];
      setMiddlePrice(middleOrderbookPrice(bestAsk, bestBid));
    });
  }, 1500);

  return {
    middlePrice,
    displayedOrderbookMarket: displayedOrderbook.serviceMarket,
    // Warn: Render should be triggered by other logic in this component
    // eslint-disable-next-line react-hooks/refs
    otherMarketOrderbookActive: !!otherMarketOrderbook.current,
  };
}
